import moment from 'moment';
import { VALIDATION_RULES, ENTRY_TYPES } from '../../../../../constants';
import { generateFieldKey } from '../../v2/components/form-template/upsert-templates/base/common';

class ReportsService {
  constructor(apiService) {
    this.apiService = apiService;
  }

  getCasesPage(params) {
    return this.apiService.get('/api/v1/reports/cases', params);
  }

  getReportsPage(params, id) {
    return this.apiService.get(`/api/v1/reports/cases/${id}/reports`, params);
  }

  getReportById(id) {
    return this.apiService.get(`/api/v1/reports/report/${id}`);
  }

  getDeletedReports(params) {
    return this.apiService.get('/api/v1/reports/report/deleted', params);
  }

  getDeletedCases(params) {
    return this.apiService.get('/api/v1/reports/cases/deleted', params);
  }

  getFeedMeesages(id) {
    return this.apiService.get(`/api/v1/reports/feed/${id}/messages`);
  }

  postFeedMeesage(id, message) {
    const p = new FormData();
    p.append('message', message);
    return this.apiService.post(`/api/v1/reports/feed/${id}/message`, p, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  getCaseById(id) {
    return this.apiService.get(`/api/v1/reports/cases/${id}`);
  }

  getTemplates(params) {
    return this.apiService.get('/api/v1/reports/form-templates', params);
  }

  getFormTemplatesPage(params) {
    return this.apiService.get('/api/v1/reports/form-templates/page', params);
  }

  getTemplateById(id) {
    return this.apiService.get(`/api/v1/reports/form-templates/${id}`);
  }

  async createFormTemplate(params) {
    const { payload, reports } = this.mapTemplatePayload(params, false, true);
    const { id } = await this.apiService.post('/api/v1/reports/form-templates', payload);

    if (reports) {
      const upload = async (g, type) => {
        const p = new FormData();
        p.append('type', type);
        p.append('file', g.file);
        p.append('key', g.id);
        await this.apiService.put(`/api/v1/reports/form-templates/${id}/file`, p, { headers: { 'Content-Type': 'multipart/form-data' } });
      };
      for (const key in reports.fields) {
        const g = reports.fields[key];
        await upload(g, 'field');
      }
    }
  }

  exportFormTemplate(params) {
    return this.apiService.post('/api/v1/reports/form-templates/export', params);
  }

  importFormTemplate(params) {
    return this.apiService.post('/api/v1/reports/form-templates/import', params);
  }

  async updateFormTemplate(params, id) {
    const { payload, reports } = this.mapTemplatePayload(params, false, true);
    await this.apiService.put(`/api/v1/reports/form-templates/${id}`, payload);

    if (reports) {
      const upload = async (g, type) => {
        const p = new FormData();
        p.append('type', type);
        p.append('file', g.file);
        p.append('key', g.id);
        await this.apiService.put(`/api/v1/reports/form-templates/${id}/file`, p, { headers: { 'Content-Type': 'multipart/form-data' } });
      };
      for (const key in reports.fields) {
        const g = reports.fields[key];
        await upload(g, 'field');
      }
    }
  }

  deleteTemplates(params) {
    return this.apiService.delete('/api/v1/reports/form-templates', params);
  }

  createCase(params) {
    const payload = this.mapNewCasePayload(params);
    return this.apiService.post('/api/v1/reports/cases', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  updateCase(params) {
    const payload = this.mapExistingCasePayload(params);
    return this.apiService.put(`/api/v1/reports/cases/${params.id}`, payload, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  importCases(file) {
    const payload = new FormData();
    payload.append('file', file);
    return this.apiService.post('/api/v1/reports/cases-import', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  importUniqueCases(file) {
    const payload = new FormData();
    payload.append('file', file);
    return this.apiService.post('/api/v1/reports/unique-cases-import', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  importAllCases(file) {
    const payload = new FormData();
    payload.append('file', file);
    return this.apiService.post('/api/v1/reports/all-cases-import', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  changeCaseStatus(params) {
    return this.apiService.patch(`/api/v1/reports/cases/${params.id}/status/${params.status}`);
  }

  downloadCaseFile(caseId, fieldKey, fileName) {
    return this.apiService.download(`/api/v1/reports/cases/${caseId}/files/${fieldKey}`, fileName);
  }

  deleteCases(params) {
    return this.apiService.delete('/api/v1/reports/cases', params);
  }

  hardDeleteReport(id) {
    return this.apiService.delete(`/api/v1/reports/report-hard/${id}`);
  }

  hardDeleteCase(id) {
    return this.apiService.delete(`/api/v1/reports/cases-hard/${id}`);
  }

  restoreReport(id) {
    return this.apiService.put(`/api/v1/reports/report/restore/${id}`);
  }

  restoreCase(id) {
    return this.apiService.put(`/api/v1/reports/case/restore/${id}`);
  }

  getTeams(params) {
    return this.apiService.get('/api/v1/teams', params);
  }

  getTeamByID(id) {
    return this.apiService.get(`/api/v1/teams/${id}`);
  }

  createTeam(params) {
    return this.apiService.post('/api/v1/teams', params);
  }

  updateTeam(id, params) {
    return this.apiService.put(`/api/v1/teams/${id}`, params);
  }

  deleteTeams(params) {
    return this.apiService.delete('/api/v1/teams', params);
  }

  createReport(caseId, checkStatus = true) {
    return this.apiService.post(
      `/api/v1/reports/cases/${caseId}/report?checkStatus=${checkStatus}`,
      {},
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  updateReport(caseId, params) {
    const payload = this.mapReportPayload(params);
    return this.apiService.put(`/api/v1/reports/cases/${caseId}/report`, payload, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  updateReportById(reportId, body) {
    const payload = this.mapReportPayload(body);
    return this.apiService.put(`/api/v1/reports/report/${reportId}`, payload, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  completeReportById(reportId, body) {
    const payload = this.mapReportPayload(body);
    return this.apiService.post(`/api/v1/reports/report/${reportId}`, payload, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  generateDocx(reportId) {
    return this.apiService.get(`/api/v1/reports/report/${reportId}/generateDocx`);
  }

  getUsersLookup() {
    return this.apiService.get('/api/v1/users');
  }

  mapTemplatePayload(params, isOne = false, isReports = false) {
    const fields = [];

    const keyList = [];

    const generateUniqueKey = (key, number) => {
      const newKey = `${key}_${number}`;
      if (keyList.includes(newKey)) {
        return generateUniqueKey(key, number + 1);
      } else {
        return newKey;
      }
    };

    const one = {
      groups: {},
      fields: {},
    };

    const reports = {
      fields: {},
    };

    const translation = {};

    const fieldsKeysById = {};

    params.groups.forEach((g) => {
      one.groups[g.id] = {
        file: g.file ? g.file : null,
        id: g.id,
      };

      if (g.translation) {
        translation[g.id] = g.translation;
      }

      g.fields.forEach((field, index) => {
        let key = field.customKey || field.key ? field.key : generateFieldKey(field.title);
        if (keyList.includes(key)) {
          if (field.customKey) {
            throw { message: `Found duplicate for key - ${key} (Title: ${field.title}). This value should be unique. Please, change it.` };
          } else {
            key = generateUniqueKey(key, 1);
          }
        }

        if (field.translation) {
          translation[key] = field.translation;
        }

        one.fields[key] = {
          file: field.file ? field.file : null,
          id: key,
        };

        keyList.push(key);

        const data = {
          key: key,
          id: field.id,
          title: field.title,
          internal: field.internal || false,
          entryType: field.type,
          description: field.description,
          defaultValue: field.defaultValue,
          unitName: field.unitName,
          unitValue: Number(field.unitValue),
          unitFieldName: field.unitFieldName,
          order: index + 1,
          groupId: g.id,
          values: field.values ? field.values.map((i) => i.value) : [],
          listId: field.listId ? field.listId : null,
          mappedValues: field.mappedValues ? field.mappedValues.map(({ key, value }) => ({ key, value })) : [],
          rules: [],
          editByMobile: field.editByMobile,
          comparisonsRules: field.comparisonsRules || null,
          requiredWhenRules: field.requiredWhenRules || null,
        };

        if (isOne) {
          data.isConfirmation = g.isConfirmation ? Boolean(field.isConfirmation) : false;
        }

        if (field.type === ENTRY_TYPES.PICTURE_IN_TEMPLATE || field.type === ENTRY_TYPES.PDF_ATTACHMENT || isOne) {
          data.file = null;

          if (field.file) {
            if (typeof field.file === 'string') {
              data.file = field.file;
              data.fileType = field.fileType;
            }
            if (typeof field.file === 'object') {
              data.file = field.file.name;
              data.fileType = field.file.type;
            }
          }

          reports.fields[key] = {
            file: field.file ? field.file : null,
            id: key,
          };
        }

        if (field.type === ENTRY_TYPES.FEED) {
          data.editByMobile = false;
        }
        if (field.type === ENTRY_TYPES.DROPDOWN) {
          data.showTextInput = field.showTextInput;
          data.textInputName = field.textInputName;
        }

        if (field.required) {
          data.rules.push({ name: VALIDATION_RULES.REQUIRED, value: null });
        }
        if (field.isAttachFileToEmail) {
          data.rules.push({ name: VALIDATION_RULES.IS_ATTACH_FILE_TO_EMAIL, value: null });
        }
        if (field.rules) {
          if (field.rules.min) {
            data.rules.push({ name: VALIDATION_RULES.MIN, value: Number(field.rules.min) });
          }
          if (field.rules.max) {
            data.rules.push({ name: VALIDATION_RULES.MAX, value: Number(field.rules.max) });
          }
          if (field.rules.limit) {
            data.rules.push({ name: VALIDATION_RULES.LIMIT, value: Number(field.rules.limit) });
          }
          if (field.rules.minLength) {
            data.rules.push({ name: VALIDATION_RULES.MIN_LENGTH, value: Number(field.rules.minLength) });
          }
          if (field.rules.length) {
            data.rules.push({ name: VALIDATION_RULES.LENGTH, value: Number(field.rules.length) });
          }
          if (field.rules.sizeInPDF) {
            data.rules.push({ name: VALIDATION_RULES.SIZE_IN_PDF, value: Number(field.rules.sizeInPDF) });
          }
          if (field.rules.automaticallySelected) {
            data.rules.push({ name: VALIDATION_RULES.AUTOMATICALLY_SELECTED, value: Number(field.rules.automaticallySelected) });
          }
        }
        fields.push(data);
      });
    });

    const result = {
      name: params.name,
      description: params.description,
      teams: params.teams,
      email: params.email,
      emailCC: params.emailCC,
      checklistNo: params.checklistNo,
      middlewareId: params.middlewareId,
      defaultTeam: params.defaultTeam,
      defaultUser: params.defaultUser,
      translation,
      groups: params.groups.map((g) => {
        const data = { id: g.id, name: g.name };
        if (isOne) {
          data.isConfirmation = g.isConfirmation;
          data.isFile = g.isFile;
          data.file = null;
          if (g.file) {
            if (typeof g.file === 'string') {
              data.file = g.file;
            }
            if (typeof g.file === 'object') {
              data.file = g.file.name;
              data.fileType = g.file.type;
            }
          }
        }
        return data;
      }),
      fields: fields.map(({ id, comparisonsRules, requiredWhenRules, rules, ...fieldData }) => {
        const resultData = { ...fieldData, rules, comparisonsRules: null };
        if (comparisonsRules && comparisonsRules.value && comparisonsRules.fieldIdentity) {
          const newComparisonsRules = {
            value: comparisonsRules.value,
            fieldKey: fields.find(({ id, key }) => key === comparisonsRules.fieldIdentity || id === comparisonsRules.fieldIdentity).key,
          };
          resultData.comparisonsRules = newComparisonsRules;
        }
        if (requiredWhenRules && requiredWhenRules.value && requiredWhenRules.fieldIdentity) {
          resultData.rules.push({
            name: VALIDATION_RULES.REQUIRED_WHEN,
            value: null,
            referenceField: fields.find(({ id, key }) => key === requiredWhenRules.fieldIdentity || id === requiredWhenRules.fieldIdentity).key,
            referenceComparison: requiredWhenRules.value,
            referenceValue: '',
          });
        }
        return resultData;
      }),
    };

    if (isOne) {
      return {
        payload: result,
        one,
      };
    }

    if (isReports) {
      return {
        payload: result,
        reports,
      };
    }

    return result;
  }

  mapExistingCasePayload(params) {
    const payload = new FormData();
    payload.append('teamId', params.team.id);
    payload.append('caseNumberString', params.caseNumberString);
    payload.append('numberSortCriterium', params.numberSortCriterium);
    payload.append('alphanumericSortCriterium', params.alphanumericSortCriterium);
    payload.append('translateLanguage', params.translateLanguage || null);
    payload.append('email', params.email || null);
    payload.append('emailCC', params.emailCC || null);
    payload.append('referenceOrInfo', params.referenceOrInfo);
    payload.append('customerEmailAddress', params.customerEmailAddress);
    payload.append('customerMobileContact', params.customerMobileContact);
    payload.append('customerName', params.customerName);
    payload.append('customerServiceHours', params.customerServiceHours);
    payload.append('customerStreetCityCountry', params.customerStreetCityCountry);
    payload.append('customerTelefoneContact', params.customerTelefoneContact);
    payload.append('dateDeal', params.dateDeal);
    payload.append('groupingCriteria', params.groupingCriteria);
    payload.append('pointOfServiceEmailAddress', params.pointOfServiceEmailAddress);
    payload.append('pointOfServiceHours', params.pointOfServiceHours);
    payload.append('pointOfServiceMobileContact', params.pointOfServiceMobileContact);
    payload.append('pointOfServiceName', params.pointOfServiceName);
    payload.append('pointOfServiceStreetCityCountry', params.pointOfServiceStreetCityCountry);
    payload.append('pointOfServiceTelefoneContact', params.pointOfServiceTelefoneContact);
    if (params.user) {
      payload.append('userId', params.user ? params.user.id : null);
    }
    payload.append('status', params.status);
    payload.append('title', params.title);

    if (params.fields) {
      params.fields.forEach((f) => {
        const key = f.key;
        const field = {
          value: f.value,
          entryType: this.getFieldType(params.template, key),
        };
        const isAttachFileToEmailRule = f.rules && f.rules.find((r) => r.name === VALIDATION_RULES.IS_ATTACH_FILE_TO_EMAIL);
        if (isAttachFileToEmailRule) {
          payload.append(`${f.key}_attachToEmail`, f.isAttachFileToEmail);
        }
        payload.append(`field_${key}`, this.mapFieldValue(field));
        if (f.showTextInput) {
          payload.append(`field_${f.key}_textInputValue`, typeof f.textInputValue !== 'undefined' ? f.textInputValue : null);
        }
      });
    }
    return payload;
  }

  getFieldType(template, key) {
    const field = template.fields.find((item) => item.key === key);
    return field.entryType;
  }
  mapNewCasePayload(params) {
    const payload = new FormData();
    payload.append('id', params.id);
    payload.append('templateId', params.templateId);
    payload.append('numberSortCriterium', params.numberSortCriterium);
    payload.append('alphanumericSortCriterium', params.alphanumericSortCriterium);
    payload.append('caseNumberString', params.caseNumberString);
    payload.append('title', params.title);
    payload.append('isQr', params.isQr);
    payload.append('expiresIn', params.expiresIn);
    payload.append('teamId', params.teamId);
    payload.append('translateLanguage', params.translateLanguage || null);
    payload.append('email', params.email || null);
    payload.append('emailCC', params.emailCC || null);
    payload.append('userId', params.userId);
    payload.append('status', params.status);
    payload.append('referenceOrInfo', params.referenceOrInfo);
    payload.append('customerEmailAddress', params.customerEmailAddress);
    payload.append('customerMobileContact', params.customerMobileContact);
    payload.append('customerName', params.customerName);
    payload.append('customerServiceHours', params.customerServiceHours);
    payload.append('customerStreetCityCountry', params.customerStreetCityCountry);
    payload.append('customerTelefoneContact', params.customerTelefoneContact);
    payload.append('dateDeal', params.dateDeal);
    payload.append('groupingCriteria', params.groupingCriteria);
    payload.append('pointOfServiceEmailAddress', params.pointOfServiceEmailAddress);
    payload.append('pointOfServiceHours', params.pointOfServiceHours);
    payload.append('pointOfServiceMobileContact', params.pointOfServiceMobileContact);
    payload.append('pointOfServiceName', params.pointOfServiceName);
    payload.append('pointOfServiceStreetCityCountry', params.pointOfServiceStreetCityCountry);
    payload.append('pointOfServiceTelefoneContact', params.pointOfServiceTelefoneContact);
    params.fields.forEach((field) => {
      const isAttachFileToEmailRule = field.rules && field.rules.find((r) => r.name === VALIDATION_RULES.IS_ATTACH_FILE_TO_EMAIL);
      if (isAttachFileToEmailRule) {
        payload.append(`${field.key}_attachToEmail`, field.isAttachFileToEmail);
      }
      payload.append(`field_${field.key}`, this.mapFieldValue(field));
      // payload.append(`field_${field.key}_description`, typeof field.description !== 'undefined' ? field.description : null);
      if (field.showTextInput) {
        payload.append(`field_${field.key}_textInputValue`, typeof field.textInputValue !== 'undefined' ? field.textInputValue : null);
      }
    });
    return payload;
  }

  mapFieldValue(field) {
    if (typeof field.value == 'undefined') {
      return null;
    }
    if (field.value === '') {
      return null;
    }
    if (field.entryType === ENTRY_TYPES.INT || field.entryType === ENTRY_TYPES.DECIMAL) {
      if (!field.value) {
        return null;
      }
    }
    if (field.entryType === ENTRY_TYPES.LIST) {
      if (typeof field.value === 'string') {
        field.value = JSON.parse(field.value);
      }
      return JSON.stringify(
        field.value
          ? field.value.map((i) => ({ id: i.id, identifier: i.identifier, title: i.title, subtitle: i.subtitle, content: i.content }))
          : field.value
      );
    }
    if (field.entryType === ENTRY_TYPES.DATE) {
      return moment(field.value).format('YYYY-MM-DD');
    }
    if (field.entryType === ENTRY_TYPES.DATE_TIME) {
      return moment.utc(field.value).local().format();
    }
    const toString = [ENTRY_TYPES.LOCATION, ENTRY_TYPES.TIME, ENTRY_TYPES.MULTIPLE_CHECKBOXES];
    if (toString.includes(field.entryType)) {
      return JSON.stringify(field.value);
    }
    return field.value;
  }

  // mapReportPayload(params) {
  //   const payload = new FormData();

  //   params.fields.forEach((field) => {
  //     payload.append(field.key, typeof field.value !== 'undefined' ? field.value : null);
  //   });

  //   return payload;
  // }

  mapReportPayload(params) {
    const payload = new FormData();

    params.fields.forEach((field) => {
      const isAttachFileToEmailRule = field.rules && field.rules.find((r) => r.name === VALIDATION_RULES.IS_ATTACH_FILE_TO_EMAIL);
      if (isAttachFileToEmailRule) {
        payload.append(`${field.key}_attachToEmail`, field.isAttachFileToEmail);
      }
      payload.append(field.key, this.mapFieldValue(field));
      if (field.showTextInput) {
        payload.append(`${field.key}_textInputValue`, typeof field.textInputValue !== 'undefined' ? field.textInputValue : null);
      }
    });

    if (params.excludeFromPDFValues) {
      Object.keys(params.excludeFromPDFValues).forEach((id) => {
        payload.append(`${id}_excludeFromPDF`, params.excludeFromPDFValues[id]);
      });
    }

    payload.append('forceValidation', Boolean(params.forceValidation));

    return payload;
  }
}

export default ReportsService;
